/**
 * SWT status panel (Hub Tasks #11/#12; Asana 1217186757080252) — shown above
 * the Proof of Student Status form. Renders ONLY applicant-safe projections
 * from client/src/lib/swtStatus.ts; nothing here blocks the step (the early
 * SWT date-window result stays a soft warning; the hard gate is Camp
 * Contract, owned by the Rules Engine).
 */
import {
  SwtAcademicVerificationRequest,
  SwtStatusBadge,
  SwtVacationVerification,
  deriveEnrollmentProofBadge,
  deriveVacationVerificationBadge,
  requestStatusLabel,
} from '../lib/swtStatus';

const toneStyle: Record<SwtStatusBadge['tone'], React.CSSProperties> = {
  pending: { background: 'var(--brand-50)', borderColor: 'var(--line)' },
  success: { background: 'var(--ok-50, #ecfdf5)', borderColor: 'var(--ok, #10b981)' },
  info: { background: 'var(--card)', borderColor: 'var(--line)' },
};

function Badge({ badge }: { badge: SwtStatusBadge }) {
  return (
    <div
      role="status"
      style={{
        ...toneStyle[badge.tone],
        border: '1px solid',
        borderRadius: 10,
        padding: '10px 14px',
        fontSize: '0.9rem',
        color: 'var(--ink)',
      }}
    >
      {badge.label}
    </div>
  );
}

export default function SwtStatusPanel({
  assessment,
  vacationVerification,
  requests,
}: {
  assessment: unknown;
  vacationVerification: SwtVacationVerification | null;
  requests: SwtAcademicVerificationRequest[];
}) {
  const proofBadge = deriveEnrollmentProofBadge(assessment);
  const vacationBadge = deriveVacationVerificationBadge(vacationVerification);
  if (!proofBadge && !vacationBadge && requests.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
      {proofBadge && <Badge badge={proofBadge} />}
      {vacationBadge && <Badge badge={vacationBadge} />}
      {vacationVerification?.verificationState === 'VERIFIED' &&
        vacationVerification.verifiedStartDate &&
        vacationVerification.verifiedEndDate && (
          <div style={{ fontSize: '0.85rem', color: 'var(--ink-2)' }}>
            Verified vacation period: {vacationVerification.verifiedStartDate} to{' '}
            {vacationVerification.verifiedEndDate}
          </div>
        )}
      {requests.length > 0 && (
        <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '10px 14px' }}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 6 }}>
            University verification requests
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {requests.map((r) => (
              <li key={r.id} style={{ fontSize: '0.85rem', color: 'var(--ink-2)' }}>
                {[r.contactName, r.contactRole].filter(Boolean).join(' — ') || 'University contact'}:{' '}
                <strong style={{ color: 'var(--ink)' }}>{requestStatusLabel(r.status)}</strong>
                {r.status === 'APPROVED' && r.confirmedVacationStart && r.confirmedVacationEnd && (
                  <> (confirmed {r.confirmedVacationStart} to {r.confirmedVacationEnd})</>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
